export const AVAILABLE_CURRENCIES = ["eur", "eur-at", "czk", "pln", "ron"];

export const DOMAIN_MAP_TO_CURRENCY = {
  si: "eur",
  hr: "eur",
  sk: "eur",
  at: "eur-at",
  de: "eur",
  cz: "czk",
  pl: "pln",
  ro: "ron",
};
export const CURRENCY_MAP_TO_CURRENCY_SYMBOL = {
  EUR: "€",
  "EUR-AT": "€",
  CZK: "Kč",
  PLN: "zł",
  RON: "Lei",
};

export const DEFAULT_CURRENCY = "eur";

export const CURRENCY_CONVERSION_TO_DEFAULT = {
  czk: 25.2,
  pln: 4.25,
  ron: 5,
  "eur-at": 1,
  eur: 1,
};

/**
 * Currencies grouped with a (no-break) space rather than a dot, as their
 * customers write them: "1 249,00 Kč", "1 000,00 zł". Euro and Lei keep the
 * dot ("1.249,00 €", "1.249,00 Lei" — Romanian groups with a dot too).
 *
 * The space is added by hand rather than via cs-CZ / pl-PL: pl-PL leaves
 * four-digit amounts ungrouped ("1000,00 zł") while five-digit ones get a
 * space, so one cart would mix both styles (currency walk B6).
 */
const SPACE_GROUPED_CURRENCIES = new Set(["CZK", "PLN"]);

export const formatCurrency = (amount: number, currency: string): string => {
  if (!currency) {
    return "";
  }

  const currencyUpper = currency.toUpperCase().split("-")[0];
  let formatted = amount.toLocaleString("de-EU", {
    style: "currency",
    currency: currencyUpper,
  });
  if (SPACE_GROUPED_CURRENCIES.has(currencyUpper)) {
    formatted = formatted.replace(/\./g, "\u00a0");
  }
  if (
    currencyUpper !== DEFAULT_CURRENCY.toUpperCase() &&
    currencyUpper in CURRENCY_MAP_TO_CURRENCY_SYMBOL
  ) {
    formatted = formatted.replace(
      currencyUpper,
      CURRENCY_MAP_TO_CURRENCY_SYMBOL[
        currencyUpper as keyof typeof CURRENCY_MAP_TO_CURRENCY_SYMBOL
      ],
    );
  }
  return formatted;
};

// ─────────────────────────────────────────────────────────────────────────────
// Pricing tables below are SEED / FALLBACK values mirroring the backend. The
// authoritative source is the backend `/pricing/shipping` and
// `/pricing/photo-options` endpoints; on app boot, `bootstrapPricing()` fetches
// them and overwrites these tables in-place. Components read via the existing
// synchronous getters and remain unchanged.
//
// These seeds are only used if the bootstrap call fails (offline / API down),
// so keep them roughly in sync with the backend
// (photo-upload-backend/src/utils/pricing/price-calculator.ts) — but the
// backend stays the single source of truth.
// Last synced: 2026-06-01 — free shipping 80 €, new shipping / COD / crop
// prices, PLN charm-priced (CZK / RON unchanged).
// ─────────────────────────────────────────────────────────────────────────────

const EURO_SHIPPING = {
  free: 0,
  normal: { home: 499, parcelLocker: 469 }, // 4.99 € home, 4.69 € parcel locker
  express: { home: 699, parcelLocker: 669 }, // 6.99 € home, 6.69 € parcel locker
};
const CZK_SHIPPING = {
  free: 0,
  normal: { home: 10950, parcelLocker: 1490 },
  express: { home: 17000, parcelLocker: 14800 },
};
const PLN_SHIPPING = {
  free: 0,
  normal: { home: 2199, parcelLocker: 1999 }, // 21.99 / 19.99 PLN (~4.99/4.69 €, charm)
  express: { home: 2999, parcelLocker: 2899 }, // 29.99 / 28.99 PLN (~6.99/6.69 €, charm)
};
const RON_SHIPPING = {
  free: 0,
  normal: { home: 2240, parcelLocker: 1790 },
  express: { home: 3500, parcelLocker: 3050 },
};
export const SHIPPING_COST_PER_COUNTRY: Record<string, typeof EURO_SHIPPING> = {
  si: { ...EURO_SHIPPING, normal: { ...EURO_SHIPPING.normal }, express: { ...EURO_SHIPPING.express } },
  hr: { ...EURO_SHIPPING, normal: { ...EURO_SHIPPING.normal }, express: { ...EURO_SHIPPING.express } },
  sk: { ...EURO_SHIPPING, normal: { ...EURO_SHIPPING.normal }, express: { ...EURO_SHIPPING.express } },
  at: { ...EURO_SHIPPING, normal: { ...EURO_SHIPPING.normal }, express: { ...EURO_SHIPPING.express } },
  de: { ...EURO_SHIPPING, normal: { ...EURO_SHIPPING.normal }, express: { ...EURO_SHIPPING.express } },
  cz: { ...CZK_SHIPPING, normal: { ...CZK_SHIPPING.normal }, express: { ...CZK_SHIPPING.express } },
  pl: { ...PLN_SHIPPING, normal: { ...PLN_SHIPPING.normal }, express: { ...PLN_SHIPPING.express } },
  ro: { ...RON_SHIPPING, normal: { ...RON_SHIPPING.normal }, express: { ...RON_SHIPPING.express } },
};

export const getShippingCost = (domain: string) => {
  if (!(domain in SHIPPING_COST_PER_COUNTRY)) {
    return EURO_SHIPPING;
  }
  const shippingCost =
    SHIPPING_COST_PER_COUNTRY[domain as keyof typeof SHIPPING_COST_PER_COUNTRY];
  return shippingCost;
};

export const getShippingCostFromType = (
  domain: string,
  shippingType: "free" | "normal" | "express",
  deliveryType?: "home" | "parcelLocker",
): number => {
  const shippingCost = getShippingCost(domain);
  const methodCost = shippingCost[shippingType];

  // If it's free shipping, return 0
  if (shippingType === "free") {
    return 0;
  }

  // If it's an object with home/parcelLocker prices, return the appropriate one
  if (typeof methodCost === "object" && methodCost !== null) {
    return methodCost[deliveryType || "home"];
  }

  // Fallback for legacy structure
  return methodCost as number;
};

// New function to get shipping price with delivery type
export const getShippingPriceByDeliveryType = (
  domain: string,
  shippingMethod: "free" | "normal" | "express",
  deliveryType: "home" | "parcelLocker" = "home",
): number => {
  return getShippingCostFromType(domain, shippingMethod, deliveryType);
};

/////////////////// Payment on delivery price ///////////////////////
const EURO_PAYMENT_ON_DELIVERY_PRICE = 149; // 1.49 €
const CZK_PAYMENT_ON_DELIVERY_PRICE = 2300;
const PLN_PAYMENT_ON_DELIVERY_PRICE = 649; // 6.49 PLN (~1.49 €, charm)
const RON_PAYMENT_ON_DELIVERY_PRICE = 450;

export const PAYMENT_ON_DELIVERY_PRICE = {
  si: EURO_PAYMENT_ON_DELIVERY_PRICE,
  hr: EURO_PAYMENT_ON_DELIVERY_PRICE,
  sk: EURO_PAYMENT_ON_DELIVERY_PRICE,
  at: EURO_PAYMENT_ON_DELIVERY_PRICE,
  de: EURO_PAYMENT_ON_DELIVERY_PRICE,
  cz: CZK_PAYMENT_ON_DELIVERY_PRICE,
  pl: PLN_PAYMENT_ON_DELIVERY_PRICE,
  ro: RON_PAYMENT_ON_DELIVERY_PRICE,
};

export const getPaymentOnDeliveryPrice = (domain: string) => {
  if (!(domain in PAYMENT_ON_DELIVERY_PRICE)) {
    return EURO_PAYMENT_ON_DELIVERY_PRICE;
  }
  return PAYMENT_ON_DELIVERY_PRICE[
    domain as keyof typeof PAYMENT_ON_DELIVERY_PRICE
  ];
};

export const AMOUNT_FOR_PAYMENT_ON_DELIVERY = {
  si: 2000,
  hr: 2000,
  sk: 2000,
  at: 0,
  de: 2000,
  cz: 50000,
  pl: 8500,
  ro: 10000,
}; // 20€

export const getAmountNeededForPaymentOnDelivery = (domain: string) => {
  if (!(domain in AMOUNT_FOR_PAYMENT_ON_DELIVERY)) {
    return 0;
  }
  return AMOUNT_FOR_PAYMENT_ON_DELIVERY[
    domain as keyof typeof AMOUNT_FOR_PAYMENT_ON_DELIVERY
  ];
};

/////////////////// Calendar pricing ///////////////////////
export enum CalendarFormat {
  A2 = "A2",
  A3 = "A3",
  A4 = "A4",
  A5 = "A5",
  PLANNER_A3 = "PLANNER_A3",
  SPREAD_A4 = "SPREAD_A4", // v2 landscape two-sheet A4 spread, priced like A3
}

// DISPLAY ONLY. These must match photo-upload-backend
// src/core/pricing/calendar-prices.ts, which is what actually gets charged.
// Nothing syncs them: bootstrapPricing() fetches shipping and photo options,
// not calendar prices, so a change here without the matching backend change
// quotes one number in the editor and charges another at checkout.
//
// Raised by 5 EUR per format on 2026-07-28, with the non-EUR tables moved by
// the equivalent at the rates below. The A2 and A4 RON values had also drifted
// from the backend (149.50 vs 125.00 charged, 99.50 vs 100.00 charged); both
// tables now carry the same numbers.
const CALENDAR_BASE_PRICES: Record<CalendarFormat, number> = {
  [CalendarFormat.A2]: 3490, // 34.90 EUR in cents
  [CalendarFormat.A3]: 2990, // 29.90 EUR in cents
  [CalendarFormat.A4]: 2490, // 24.90 EUR in cents
  [CalendarFormat.A5]: 2000, // 20.00 EUR in cents
  [CalendarFormat.PLANNER_A3]: 1000, // 10.00 EUR — one A3 sheet, not a 13-month block
  [CalendarFormat.SPREAD_A4]: 2990, // 29.90 EUR, same as A3
};

const CALENDAR_CURRENCY_PRICES: Record<
  CalendarFormat,
  { [currency: string]: number }
> = {
  [CalendarFormat.A2]: {
    czk: 87250, // ~34.90 EUR * 25 CZK/EUR
    pln: 15360, // ~34.90 EUR * 4.4 PLN/EUR
    ron: 17450, // ~34.90 EUR * 5 RON/EUR
  },
  [CalendarFormat.A3]: {
    czk: 74750, // ~29.90 EUR * 25 CZK/EUR
    pln: 13160, // ~29.90 EUR * 4.4 PLN/EUR
    ron: 14950, // ~29.90 EUR * 5 RON/EUR
  },
  [CalendarFormat.A4]: {
    czk: 62250, // ~24.90 EUR * 25 CZK/EUR
    pln: 10950, // ~24.90 EUR * 4.4 PLN/EUR
    ron: 12450, // ~24.90 EUR * 5 RON/EUR
  },
  [CalendarFormat.A5]: {
    czk: 50000, // ~20 EUR * 25 CZK/EUR
    pln: 8800, // ~20 EUR * 4.4 PLN/EUR
    ron: 10000, // ~20 EUR * 5 RON/EUR
  },
  [CalendarFormat.PLANNER_A3]: {
    czk: 25000, // ~10 EUR * 25 CZK/EUR
    pln: 4400, // ~10 EUR * 4.4 PLN/EUR
    ron: 5000, // ~10 EUR * 5 RON/EUR
  },
  [CalendarFormat.SPREAD_A4]: {
    czk: 74750,
    pln: 13160,
    ron: 14950,
  },
};

export const getCalendarPrice = (
  format: CalendarFormat,
  currency: string,
): number => {
  const currencyLower = currency.toLowerCase().split("-")[0];

  if (currencyLower !== DEFAULT_CURRENCY) {
    return (
      CALENDAR_CURRENCY_PRICES[format]?.[currencyLower] ||
      CALENDAR_BASE_PRICES[format]
    );
  }

  return CALENDAR_BASE_PRICES[format];
};

// Paper surcharge per calendar, in cents. Mirrors the backend table in
// src/core/pricing/calendar-prices.ts — ship both together.
const CALENDAR_PAPER_SURCHARGE: Record<
  "white" | "silver" | "gold",
  Record<string, number>
> = {
  white: { eur: 0, czk: 0, pln: 0, ron: 0 },
  silver: { eur: 200, czk: 5000, pln: 880, ron: 1000 },
  gold: { eur: 200, czk: 5000, pln: 880, ron: 1000 },
};

export const getCalendarPaperSurchargeCents = (
  paper: "white" | "silver" | "gold",
  currency: string,
): number =>
  CALENDAR_PAPER_SURCHARGE[paper][currency.toLowerCase().split("-")[0]] ??
  CALENDAR_PAPER_SURCHARGE[paper].eur;

/////////////////// Additional pricing options ///////////////////////
// Per-currency extras tables. Indexed by upper-case currency code (without
// region suffix), e.g. "EUR", "CZK", "PLN", "RON". Mutated by
// `bootstrapPricing()` from /pricing/photo-options at app boot.
export const ECOLOGICAL_PACKAGING_PRICE: Record<string, number> = {
  EUR: 100, CZK: 2500, PLN: 430, RON: 500,
};
export const PRIORITY_COMPLAINT_PRICE: Record<string, number> = {
  EUR: 0,   CZK: 0,    PLN: 0,   RON: 0,
};
export const WRAP_AS_GIFT_PRICE: Record<string, number> = {
  EUR: 600, CZK: 15000, PLN: 2580, RON: 3000,
};
export const PROFESSIONAL_CROP_PRICE: Record<string, number> = {
  EUR: 389, CZK: 5000, PLN: 1699, RON: 1000,
};

function lookupExtra(table: Record<string, number>, currency: string): number {
  const upper = currency.toUpperCase().split("-")[0];
  return table[upper] ?? table.EUR;
}

/////////////////// Free shipping thresholds ///////////////////////
const EURO_FREE_SHIPPING_THRESHOLD = 8000; // 80.00 EUR
// 180.00 PLN. Was 339.00 (the ~80 EUR line every other market uses), which
// 2.0% of paid Polish orders ever reached against 11.1% in Slovenia — an
// incentive nobody could see. Must match the backend, which is what actually
// charges: photo-upload-backend/src/utils/pricing/price-calculator.ts.
const PLN_FREE_SHIPPING_THRESHOLD = 18000;
const CZK_FREE_SHIPPING_THRESHOLD = 150000; // 1500.00 CZK (~60 EUR) — matches backend
const RON_FREE_SHIPPING_THRESHOLD = 30000; // 300.00 RON (~60 EUR)

export const FREE_SHIPPING_THRESHOLD = {
  si: EURO_FREE_SHIPPING_THRESHOLD,
  hr: EURO_FREE_SHIPPING_THRESHOLD,
  sk: EURO_FREE_SHIPPING_THRESHOLD,
  at: EURO_FREE_SHIPPING_THRESHOLD,
  de: EURO_FREE_SHIPPING_THRESHOLD,
  cz: CZK_FREE_SHIPPING_THRESHOLD,
  pl: PLN_FREE_SHIPPING_THRESHOLD,
  ro: RON_FREE_SHIPPING_THRESHOLD,
};

/////////////////// Gift thresholds ///////////////////////
// A free 30 × 40 enlargement once the goods (shipping excluded, before credit
// — the same base as free shipping) reach 50 EUR. A step between the ~32 EUR
// average basket and the 80 EUR free-shipping line, which ~11 % of Slovenian
// orders reach: close enough to chase. Euro markets only for now; Poland,
// Czechia and Romania have lower shipping lines, so a gift above them would
// sit in the wrong order. Must match the backend, which grants it:
// photo-upload-backend/src/utils/pricing/gift.ts.
const EURO_GIFT_THRESHOLD = 5000; // 50.00 EUR

export const GIFT_THRESHOLD: Partial<Record<string, number>> = {
  si: EURO_GIFT_THRESHOLD,
  hr: EURO_GIFT_THRESHOLD,
  sk: EURO_GIFT_THRESHOLD,
  at: EURO_GIFT_THRESHOLD,
  de: EURO_GIFT_THRESHOLD,
};

export const getFreeShippingThreshold = (domain: string): number => {
  if (!(domain in FREE_SHIPPING_THRESHOLD)) {
    return EURO_FREE_SHIPPING_THRESHOLD;
  }
  return FREE_SHIPPING_THRESHOLD[
    domain as keyof typeof FREE_SHIPPING_THRESHOLD
  ];
};

export const getEcologicalPackagingPrice = (currency: string): number =>
  lookupExtra(ECOLOGICAL_PACKAGING_PRICE, currency);

export const getPriorityComplaintPrice = (currency: string): number =>
  lookupExtra(PRIORITY_COMPLAINT_PRICE, currency);

export const getWrapAsGiftPrice = (currency: string): number =>
  lookupExtra(WRAP_AS_GIFT_PRICE, currency);

export const getProfessionalCropPrice = (currency: string): number =>
  lookupExtra(PROFESSIONAL_CROP_PRICE, currency);

// ─────────────────────────────────────────────────────────────────────────────
// Backend bootstrap: fetch /pricing/shipping (per country) and
// /pricing/photo-options (per currency), then mutate the tables above so all
// existing synchronous getters return the API values. Designed to run once at
// app boot from the PricingIntegrationProvider.
// ─────────────────────────────────────────────────────────────────────────────

interface PricingApiSubset {
  pricingControllerGetShippingRates: (
    country: string,
    currency: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options?: any,
  ) => Promise<{ data: ShippingRatesResponse } & object>;
  pricingControllerGetPhotoOptions: (
    currency: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options?: any,
  ) => Promise<{ data: PhotoOptionsResponse } & object>;
  // Present on clients generated after the /pricing/bootstrap endpoint landed.
  // When available it replaces the 13 per-country / per-currency calls with one.
  pricingControllerGetBootstrap?: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options?: any,
  ) => Promise<{ data: PricingBootstrapResponse } & object>;
}

interface ShippingRatesResponse {
  currency: string;
  country: string;
  methods: Array<{
    method: "free" | "normal" | "express";
    deliveryType: "HOME" | "PARCEL_LOCK" | null;
    priceCents: number;
  }>;
  codFeeCents: number;
  freeShippingThresholdCents: number | null;
}

interface PhotoOptionsResponse {
  currency: string;
  options: Array<{
    key: string;
    pricing: { kind: "flat" | "perPhoto" | "varies"; cents?: number | null };
  }>;
}

interface PricingBootstrapResponse {
  shipping: ShippingRatesResponse[];
  photoOptions: PhotoOptionsResponse[];
}

const COUNTRIES_TO_BOOTSTRAP = ["si", "hr", "sk", "at", "de", "cz", "pl", "ro"];
const CURRENCIES_TO_BOOTSTRAP = ["eur", "eur-at", "czk", "pln", "ron"];

let bootstrapPromise: Promise<void> | null = null;

// Shape of one bootstrap fetch. Cached verbatim so the apply step below runs
// identically whether the data came from the network or from localStorage.
interface ShippingBootstrapEntry {
  country: string;
  rates: ShippingRatesResponse;
}
interface OptionsBootstrapEntry {
  currency: string;
  opts: PhotoOptionsResponse;
}
interface BootstrapPayload {
  shape: string;
  ts: number;
  shipping: ShippingBootstrapEntry[];
  options: OptionsBootstrapEntry[];
}

// Persist the bootstrap so warm page loads skip the per-country/per-currency
// fan-out (13 requests → 0). These values are effectively static — they are
// display-only "from" prices and shipping estimates; the amount actually
// charged is recomputed by the backend at order time — so a short TTL is safe.
// Bump the version suffix if the response shape changes.
const PRICING_CACHE_KEY = "fd_pricing_bootstrap_v1";
const PRICING_CACHE_TTL_MS = 12 * 60 * 60 * 1000; // 12h
// Included in the cache so changing the country/currency lists auto-invalidates
// any payload written by an older build.
const PRICING_CACHE_SHAPE = `${COUNTRIES_TO_BOOTSTRAP.join(",")}|${CURRENCIES_TO_BOOTSTRAP.join(",")}`;

function applyBootstrap(
  shipping: ShippingBootstrapEntry[],
  options: OptionsBootstrapEntry[],
): void {
  for (const { country, rates } of shipping) {
    const slot = SHIPPING_COST_PER_COUNTRY[country];
    if (slot) {
      for (const m of rates.methods) {
        if (m.method === "free") slot.free = m.priceCents;
        if (m.method === "normal" && m.deliveryType === "HOME")
          slot.normal.home = m.priceCents;
        if (m.method === "normal" && m.deliveryType === "PARCEL_LOCK")
          slot.normal.parcelLocker = m.priceCents;
        if (m.method === "express" && m.deliveryType === "HOME")
          slot.express.home = m.priceCents;
        if (m.method === "express" && m.deliveryType === "PARCEL_LOCK")
          slot.express.parcelLocker = m.priceCents;
      }
    }
    if (country in PAYMENT_ON_DELIVERY_PRICE) {
      (PAYMENT_ON_DELIVERY_PRICE as Record<string, number>)[country] =
        rates.codFeeCents;
    }
    if (
      rates.freeShippingThresholdCents != null &&
      country in FREE_SHIPPING_THRESHOLD
    ) {
      (FREE_SHIPPING_THRESHOLD as Record<string, number>)[country] =
        rates.freeShippingThresholdCents;
    }
  }

  const extrasTable: Record<string, Record<string, number>> = {
    ecologicalPackaging: ECOLOGICAL_PACKAGING_PRICE,
    priorityComplaintFree: PRIORITY_COMPLAINT_PRICE,
    wrapAsGift: WRAP_AS_GIFT_PRICE,
    professionalCrop: PROFESSIONAL_CROP_PRICE,
  };

  for (const { currency, opts } of options) {
    const upper = currency.toUpperCase().split("-")[0];
    for (const opt of opts.options) {
      const target = extrasTable[opt.key];
      if (target && opt.pricing.kind === "flat" && opt.pricing.cents != null) {
        target[upper] = opt.pricing.cents;
      }
    }
  }
}

function readPricingCache(): BootstrapPayload | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PRICING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BootstrapPayload;
    if (
      parsed?.shape !== PRICING_CACHE_SHAPE ||
      typeof parsed.ts !== "number" ||
      Date.now() - parsed.ts > PRICING_CACHE_TTL_MS ||
      !Array.isArray(parsed.shipping) ||
      !Array.isArray(parsed.options)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null; // corrupt JSON / access denied → fall back to a fresh fetch
  }
}

function writePricingCache(
  shipping: ShippingBootstrapEntry[],
  options: OptionsBootstrapEntry[],
): void {
  if (typeof window === "undefined") return;
  try {
    const payload: BootstrapPayload = {
      shape: PRICING_CACHE_SHAPE,
      ts: Date.now(),
      shipping,
      options,
    };
    window.localStorage.setItem(PRICING_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // Private mode / quota exceeded — non-fatal, we just refetch next load.
  }
}

// Fetch the bootstrap tables. Prefer the single /pricing/bootstrap request; if
// that endpoint isn't on the client/backend yet (frontend and backend deploy
// independently) or it fails, fall back to the per-country / per-currency calls
// so pricing still syncs — no regression against an older backend.
async function fetchBootstrap(api: PricingApiSubset): Promise<{
  shipping: ShippingBootstrapEntry[];
  options: OptionsBootstrapEntry[];
}> {
  if (api.pricingControllerGetBootstrap) {
    try {
      const data = (await api.pricingControllerGetBootstrap()).data;
      return {
        shipping: data.shipping.map((rates) => ({
          country: rates.country,
          rates,
        })),
        options: data.photoOptions.map((opts) => ({
          currency: opts.currency,
          opts,
        })),
      };
    } catch (err) {
      // Only fall back when the endpoint genuinely is not there (a backend
      // older than /pricing/bootstrap). Anything else — a network abort from a
      // backgrounded tab or a blocking in-app browser, a timeout, a 5xx — means
      // the 13 per-item calls are going to fail exactly the same way, so firing
      // them turns one dead request into fourteen. That amplification was the
      // top "N+1 API Call" issue in Sentry (~1000 events across the home and
      // summary pages): every span in it had http status 0, i.e. the whole fan
      // out was already doomed. Re-throw so the caller resets bootstrapPromise,
      // keeps the seed tables, and can retry later.
      const status = (err as { response?: { status?: number } })?.response
        ?.status;
      if (status !== 404) throw err;
      console.warn(
        "[bootstrapPricing] /pricing/bootstrap not available on this backend; falling back to per-item calls:",
        err,
      );
    }
  }

  const [shippingResults, optionsResults] = await Promise.all([
    Promise.allSettled(
      COUNTRIES_TO_BOOTSTRAP.map(async (country) => ({
        country,
        rates: (await api.pricingControllerGetShippingRates(country, "")).data,
      })),
    ),
    Promise.allSettled(
      CURRENCIES_TO_BOOTSTRAP.map(async (currency) => ({
        currency,
        opts: (await api.pricingControllerGetPhotoOptions(currency)).data,
      })),
    ),
  ]);

  return {
    shipping: shippingResults.flatMap((r) =>
      r.status === "fulfilled" ? [r.value] : [],
    ),
    options: optionsResults.flatMap((r) =>
      r.status === "fulfilled" ? [r.value] : [],
    ),
  };
}

export function bootstrapPricing(api: PricingApiSubset): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  bootstrapPromise = (async () => {
    // Warm cache → apply and skip all network calls (kills the N+1 on repeat
    // loads within the TTL).
    const cached = readPricingCache();
    if (cached) {
      applyBootstrap(cached.shipping, cached.options);
      return;
    }

    const { shipping, options } = await fetchBootstrap(api);

    applyBootstrap(shipping, options);

    // Only persist a fully successful bootstrap so a partial fetch (some slots
    // still on seed values) doesn't get pinned for the whole TTL.
    if (
      shipping.length === COUNTRIES_TO_BOOTSTRAP.length &&
      options.length === CURRENCIES_TO_BOOTSTRAP.length
    ) {
      writePricingCache(shipping, options);
    }
  })().catch((err) => {
    // Reset so a retry is possible; keep seed values as fallback.
    console.warn("[bootstrapPricing] failed; using seed values:", err);
    bootstrapPromise = null;
  });
  return bootstrapPromise;
}
